import { appendToken } from '../lib/geoInfo';
import { isAgreementNotFoundError, AGREEMENT_NOT_IN_COMPANY_MESSAGE } from '../utils/agreements';

const DEFAULT_BASE_URL = 'https://temcontrol.com:8090';
const BASE_URL = (process.env.REACT_APP_API_URL || DEFAULT_BASE_URL).replace(/\/+$/, '');

const TOKEN_KEY = 'jornada_jwt';
const REMEMBER_EMAIL_KEY = 'jornada_remember_email';

// Almacenamiento del JWT con dos modos:
//   · persistent=true  → localStorage. La sesion sobrevive al cierre del
//     navegador y se mantiene en futuras visitas. Es el modo "Recuerdame".
//   · persistent=false → sessionStorage. La sesion vive solo mientras la
//     pestana este abierta; al cerrarla, el token se borra y hay que
//     volver a loguearse.
// `getToken()` lee de localStorage primero (sesion persistida) y cae a
// sessionStorage si no hay nada. `clearToken()` borra de ambos.
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null;
  } catch (_) {
    return null;
  }
};

export const setToken = (token, persistent = true) => {
  if (token) sessionEnding = false; // sesión nueva → se rearma el cierre por 401
  try {
    // Limpiamos el otro storage para no dejar huerfanos del modo anterior.
    if (persistent) {
      sessionStorage.removeItem(TOKEN_KEY);
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      if (token) sessionStorage.setItem(TOKEN_KEY, token);
      else sessionStorage.removeItem(TOKEN_KEY);
    }
  } catch (_) { /* storages bloqueados → no rompemos */ }
};

export const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch (_) {}
};

// Sustituye el token (renovación) respetando el modo actual: si la sesión
// vivía en sessionStorage ("Recordarme" desmarcado) sigue ahí.
export const replaceToken = (token) => {
  let persistent = true;
  try {
    persistent = !!localStorage.getItem(TOKEN_KEY) || !sessionStorage.getItem(TOKEN_KEY);
  } catch (_) {}
  setToken(token, persistent);
};

// ── Sesión caducada / deshabilitada ────────────────────────────────────────
// El backend responde 401 { code: TOKEN_EXPIRED | TOKEN_INVALID | USER_DISABLED }
// cuando el token caduca, es inválido o el usuario ya no puede entrar. Ante
// CUALQUIER 401 de una petición que llevaba token (salvo /login, donde 401 =
// credenciales mal) se cierra la sesión y AuthContext deja user = null (las
// rutas protegidas redirigen a /login, que muestra el mensaje).
// MISMA LÓGICA en TemControlApp/src/services/api.js.
export const SESSION_EXPIRED_MESSAGE = 'Tu sesión ha caducado. Vuelve a iniciar sesión.';
export const SESSION_DISABLED_MESSAGE = 'Tu acceso está deshabilitado. Contacta con tu empresa.';
export const sessionEndedMessage = (code) =>
  (code === 'USER_DISABLED' ? SESSION_DISABLED_MESSAGE : SESSION_EXPIRED_MESSAGE);

let sessionEndedHandler = null;
let sessionEnding = false;
// AuthContext registra aquí su callback ({ code, message }) => void.
export const setSessionEndedHandler = (fn) => {
  sessionEndedHandler = typeof fn === 'function' ? fn : null;
};

// Solo actúa si el token de la petición sigue siendo el ACTUAL (una respuesta
// tardía de la sesión anterior no tumba una sesión nueva) y una única vez por
// sesión (N peticiones en paralelo con 401 → un solo cierre y una redirección).
const endSession = (sentToken, code) => {
  if (!sentToken || sessionEnding || getToken() !== sentToken) return;
  sessionEnding = true;
  clearToken();
  try { sessionEndedHandler?.({ code: code || null, message: sessionEndedMessage(code) }); } catch (_) {}
};

const isLoginPath = (path) => path === '/login' || String(path).startsWith('/login?');

const codeFromBody = (text) => {
  try { const p = JSON.parse(text); return p?.code ? String(p.code) : null; } catch { return null; }
};

// Email recordado independientemente del token. Si el usuario marca
// "Recuerdame" guardamos su email para pre-rellenar el form la proxima
// vez. Si lo desmarca, lo limpiamos en el siguiente login.
export const getRememberedEmail = () => {
  try { return localStorage.getItem(REMEMBER_EMAIL_KEY) || ''; }
  catch (_) { return ''; }
};
export const setRememberedEmail = (email) => {
  try {
    if (email) localStorage.setItem(REMEMBER_EMAIL_KEY, email);
    else localStorage.removeItem(REMEMBER_EMAIL_KEY);
  } catch (_) {}
};

export class ApiError extends Error {
  constructor({ message, url, status = null, bodySnippet = null, reason = null, data = null }) {
    super(message);
    this.name = 'ApiError';
    this.url = url;
    this.status = status;
    this.bodySnippet = bodySnippet;
    this.reason = reason;
    // Body JSON completo del error (si lo hubo). `bodySnippet` está truncado a
    // 400 caracteres y no sirve para leer listas de errores de validación
    // (p.ej. `errors: [{ index, field, message }]` del import de festivos).
    this.data = data;
  }
}

// Mapea mensajes de error conocidos del backend (en inglés) a castellano,
// para que el banner que ve el usuario sea entendible. NUNCA exponemos
// "código 4xx/5xx" en pantalla — el `status` queda en ApiError para
// debugging, pero el `message` siempre es friendly.
const translateBackendMessage = (msg, status) => {
  const m = msg ? String(msg).trim() : '';
  const lower = m.toLowerCase();

  // Mensajes específicos que el backend envía como texto. Estos tienen
  // prioridad sobre el fallback por código HTTP, porque "Invalid username"
  // viene como 401 pero significa "credenciales mal", no "sesión caducada".
  if (lower.includes('invalid username') || lower.includes('invalid password')) {
    return 'Usuario o contraseña incorrectos.';
  }
  // Festivos
  if (lower.includes('holyday already exists') || lower.includes('holiday already exists')) {
    return 'Ya hay un festivo registrado en esa fecha y comunidad. No se pueden añadir dos festivos en la misma fecha.';
  }
  if (lower.includes('no holydays found') || lower.includes('no holidays found')) {
    return 'No se encontraron festivos para los criterios indicados.';
  }
  if (lower.includes('holyday deleted') || lower.includes('holiday deleted')) return 'Festivo eliminado correctamente.';
  // Trabajadores / empresas / sedes
  if (lower.includes('email already exists') || lower.includes('user already exists')) {
    return 'Ya existe un usuario con ese email, NIF o teléfono.';
  }
  if (lower.includes('cif already exists') || lower.includes('company already exists')) {
    return 'Ya existe una empresa con ese CIF.';
  }
  if (lower.includes('not found')) {
    return 'No se encontró el recurso solicitado.';
  }
  // Validaciones de body
  if (lower.includes('content-length') || lower.includes('content_length')) {
    return 'Hubo un problema enviando los datos. Vuelve a intentarlo.';
  }
  if (lower.includes('validation') && lower.includes('not a valid integer')) {
    return 'Falta un valor numérico obligatorio o tiene formato incorrecto.';
  }
  if (lower.includes('unauthorized')) {
    return 'Tu sesión ha caducado. Vuelve a iniciar sesión.';
  }
  // Rechazo por hora de entrada. Origen: Temcontrol-back routes/pikachuRouter.js:35-40,
  // mensaje literal: "Cannot add event before person's start hour with enforceStartHour = TRUE".
  // Dos anzuelos independientes a propósito: si cambia la frase pero se conserva el nombre
  // del flag (o al revés), la traducción sigue funcionando.
  // DUPLICADO A PROPÓSITO en el otro repo:
  //   TemControlApp/src/services/api.js   -> translateBackendMessage
  //   TemControlFront/src/services/api.js -> translateBackendMessage
  // Si cambias esta regla, cambia la otra.
  if (lower.includes("before person's start hour") || lower.includes('enforcestarthour')) {
    return 'Aún no puedes fichar: tu cuenta tiene activado el control de hora de entrada y el '
         + 'servidor considera que tu jornada todavía no ha empezado. Si crees que es un error, '
         + 'avisa a tu responsable.';
  }

  // Fallback por código HTTP — sin mostrar el número al usuario.
  switch (status) {
    case 400:
      // Si el backend dio un motivo y ninguna regla textual lo reconoció, se muestra
      // el motivo. Un mensaje en inglés es peor UX que uno en castellano, pero es
      // MUCHO mejor que "revisa los datos", que es falso: no hay nada que revisar.
      return m
        ? `El servidor ha rechazado la operación: ${m.length > 200 ? `${m.slice(0, 200)}...` : m}`
        : 'La información enviada no es válida. Revisa los datos e inténtalo de nuevo.';
    case 401: return 'Tu sesión ha caducado o las credenciales son incorrectas.';
    case 403: return 'No tienes permiso para realizar esta acción.';
    case 404: return 'No se encontró el recurso solicitado.';
    case 409: return 'Ya existe un registro con esos datos.';
    case 422: return 'Algunos campos no son válidos. Revisa los datos.';
    case 500:
    case 502:
    case 503:
    case 504: return 'El servidor está teniendo problemas. Inténtalo en unos minutos.';
    default:  return 'No se pudo completar la operación. Inténtalo de nuevo.';
  }
};

// Escapa todos los caracteres no-ASCII como \uXXXX. Necesario porque el
// backend (Fastify) tiene un bug por el cual rechaza con
// FST_ERR_CTP_INVALID_CONTENT_LENGTH cuando el body JSON contiene bytes
// UTF-8 multi-byte. El JSON.parse del lado servidor decodifica los escapes
// Unicode de vuelta a los caracteres originales sin pérdida.
const asciiSafeJson = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/[-￿]/g, (c) =>
    '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4)
  );
};

const request = async (path, options = {}) => {
  const url = `${BASE_URL}${path}`;
  const token = getToken();
  // Si llega un body string JSON, lo pasamos a ASCII puro (escapes Unicode).
  const safeOptions = { ...options };
  if (typeof options.body === 'string') {
    safeOptions.body = asciiSafeJson(options.body);
  }
  let res;
  try {
    res = await fetch(url, {
      ...safeOptions,
      // Sin cache HTTP: la app re-consulta datos en caliente tras crear/editar
      // (p.ej. añadir una versión de horario y refrescar la bolsa). Con el
      // cache por defecto, el navegador servía la respuesta GET antigua y había
      // que recargar la página entera. `no-store` fuerza ir siempre al servidor.
      cache: 'no-store',
      headers: {
        // Sólo añadimos Content-Type cuando realmente mandamos body. Fastify
        // rechaza con FST_ERR_CTP_EMPTY_JSON_BODY las peticiones DELETE/GET
        // que llevan el header pero ningún body, así que solo lo seteamos
        // cuando hay payload que enviar.
        ...(safeOptions.body != null ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
  } catch (_err) {
    throw new ApiError({
      message: 'No hay conexión con el servidor. Revisa tu conexión a internet.',
      url,
      reason: 'network',
    });
  }

  const contentType = res.headers.get('content-type') || '';
  let fullText = '';
  try { fullText = await res.text(); } catch {}
  const bodySnippet = fullText.slice(0, 400);

  if (!res.ok) {
    // Si el backend devuelve JSON con un `message`, preferimos ese mensaje al
    // genérico — así el usuario ve la causa real (colisiones, validaciones,
    // etc.). Y traducimos los mensajes conocidos al castellano.
    let backendMessage = null;
    let errorData = null;
    if (contentType.includes('application/json')) {
      try {
        const parsed = JSON.parse(fullText);
        errorData = parsed && typeof parsed === 'object' ? parsed : null;
        if (parsed?.message) backendMessage = String(parsed.message);
        else if (parsed?.error) backendMessage = String(parsed.error);
      } catch {}
    }
    // Sesión muerta (caducada, inválida o usuario deshabilitado) → cierre global.
    if (res.status === 401 && token && !isLoginPath(path)) {
      const errorCode = errorData?.code ? String(errorData.code) : null;
      endSession(token, errorCode);
      throw new ApiError({
        message: sessionEndedMessage(errorCode),
        url, status: 401, bodySnippet, reason: 'session-ended', data: errorData,
      });
    }
    throw new ApiError({
      message: translateBackendMessage(backendMessage, res.status),
      url,
      status: res.status,
      bodySnippet,
      reason: res.status === 401 ? 'unauthorized' : 'http-error',
      data: errorData,
    });
  }

  if (res.status === 204 || fullText.trim() === '') return null;

  if (!contentType.includes('application/json')) {
    throw new ApiError({
      message: 'No se pudo procesar la respuesta del servidor.',
      url, status: res.status, bodySnippet, reason: 'not-json',
    });
  }

  try {
    return JSON.parse(fullText);
  } catch {
    throw new ApiError({
      message: 'No se pudo procesar la respuesta del servidor.',
      url, status: res.status, bodySnippet, reason: 'parse-error',
    });
  }
};

export const loginApi = (email, password) =>
  request('/login', { method: 'POST', body: JSON.stringify({ email, password }) });

// Renueva el token (30 días) con los datos actuales del usuario → { token }.
// Un backend antiguo sin la ruta responde 404/500: el caller lo ignora.
export const refreshSessionApi = () =>
  request('/api/auth/refresh', { method: 'POST' });

// ── Recuperación de contraseña ────────────────────────────────────────────
// Flujo en dos pasos:
//   1) forgotPassword(email): el backend envía un email al usuario con un
//      enlace tipo https://temcontrol.com/reset-password?email=X&auth=TOKEN.
//   2) resetPassword({ email, newPassword, auth }): el usuario llega a esa
//      URL desde el email, el frontend lee email+auth de la query string y
//      llama a este endpoint con la nueva contraseña.
// No requieren JWT — `request` no añade Authorization si no hay token, y el
// backend los expone públicos.
export const forgotPassword = (email) =>
  request('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export const resetPassword = ({ email, newPassword, auth }) =>
  request('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email, newPassword, auth }),
  });

export const EVENT_TYPES = {
  CLOCK_IN: 0, CLOCK_OUT: 1,
  VACATION_START: 2, VACATION_END: 3,
  SICK_LEAVE_START: 4, SICK_LEAVE_END: 5,
  PAUSE_START: 6, PAUSE_END: 7,
  COMPENSATION_START: 8, COMPENSATION_END: 9,
  // Ausencia justificada por horas (médico, funeral, etc.). Backend debe
  // soportar estos tipos como un par START/END con aprovalStatus PENDING/
  // APPROVED/REJECTED, igual que vacaciones y bajas.
  ABSENCE_START: 10, ABSENCE_END: 11,
};

export const EVENT_TYPE_LABELS = {
  [EVENT_TYPES.CLOCK_IN]: 'Entrada',
  [EVENT_TYPES.CLOCK_OUT]: 'Salida',
  [EVENT_TYPES.VACATION_START]: 'Inicio vacaciones',
  [EVENT_TYPES.VACATION_END]: 'Fin vacaciones',
  // Nota: aunque la constante interna sigue siendo SICK_LEAVE_*, en la UI
  // lo llamamos "Ausencia de día completo justificada" porque no siempre es
  // una baja médica — puede ser boda, funeral, asuntos propios, etc. Los
  // event types del backend NO se renombran para no romper datos existentes.
  [EVENT_TYPES.SICK_LEAVE_START]: 'Inicio día completo',
  [EVENT_TYPES.SICK_LEAVE_END]: 'Fin día completo',
  [EVENT_TYPES.PAUSE_START]: 'Inicio pausa',
  [EVENT_TYPES.PAUSE_END]: 'Fin pausa',
  [EVENT_TYPES.COMPENSATION_START]: 'Inicio compensación',
  [EVENT_TYPES.COMPENSATION_END]: 'Fin compensación',
  [EVENT_TYPES.ABSENCE_START]: 'Inicio por horas',
  [EVENT_TYPES.ABSENCE_END]: 'Fin por horas',
};

export const APPROVAL_STATUS = {
  PENDING: 0,
  APPROVED: 1,
  REJECTED: 2,
};

export const APPROVAL_STATUS_LABELS = {
  [APPROVAL_STATUS.PENDING]: 'Pendiente',
  [APPROVAL_STATUS.APPROVED]: 'Aprobado',
  [APPROVAL_STATUS.REJECTED]: 'Rechazado',
};

export const searchCompanies = (query = '') =>
  request(`/api/company/${encodeURIComponent(query)}`);

export const createCompany = (company) =>
  request('/api/company', { method: 'POST', body: JSON.stringify(company) });

export const updateCompany = (id, company) =>
  request(`/api/company/${id}`, { method: 'PUT', body: JSON.stringify(company) });

export const deleteCompany = (id) =>
  request(`/api/company/${id}`, { method: 'DELETE' });

export const getCompanyWorkers = (companyId) =>
  request(`/api/company/${companyId}/workers`);

export const searchWorkers = (query = '') =>
  request(`/api/company/person/${encodeURIComponent(query)}`);

// Ficha del usuario logueado (sin contraseña). Sustituye a searchWorkers('')
// + buscar el propio id: el backend ya no devuelve personas de otras empresas.
// Un backend antiguo sin /api/me responde 500 (su notFoundHandler) o 404.
export const getMe = () => request('/api/me');

// BUG CONOCIDO del backend (PUT /api/company/person/:id):
// El handler responde 200 OK pero IGNORA `isLoginEnabled` según la empresa.
// Causa exacta (companyRouter.js): la asignación vive DENTRO de
// `if (company && company.licenses)`, así que:
//   · licencias = 0 (sin límite) → el campo NO se guarda nunca. 17 de las 25
//     empresas de producción están así.
//   · con límite → se guarda, salvo que ACTIVAR a alguien superara el tope
//     (ahí el servidor solo saca un console.warn y sigue como si nada).
// Medido el 01/10/2026 en producción: "TemControl Demo" (licencias 6) guarda
// la baja; "ASOCIACIÓN PAZ Y BIEN" (licencias 0) NO. NO hay workaround de
// cliente: el compi tiene que sacar la asignación de ese `if`.
// `wantsGPSemails` sí se asigna en el handler, pero el PUT NO lo devuelve en
// su respuesta, así que tampoco se puede dar por guardado mirando el 200.
// Mientras no esté arreglado, el panel RELEE la ficha después de guardar y
// avisa de lo que no ha cuajado (ver utils/workerSaveCheck.js).
export const addWorker = (worker) =>
  request('/api/company/person', { method: 'POST', body: JSON.stringify(worker) });

export const updateWorker = (id, worker) =>
  request(`/api/company/person/${id}`, { method: 'PUT', body: JSON.stringify(worker) });

export const deleteWorker = (id) =>
  request(`/api/company/person/${id}`, { method: 'DELETE' });

export const getWorkplaces = (companyId) =>
  request(`/api/company/${companyId}/workplaces`);

export const createWorkplace = (companyId, workplace) =>
  request(`/api/company/${companyId}/workplaces`, { method: 'POST', body: JSON.stringify(workplace) });

export const updateWorkplace = (id, workplace) =>
  request(`/api/company/workplaces/${id}`, { method: 'PUT', body: JSON.stringify(workplace) });

export const deleteWorkplace = (id) =>
  request(`/api/company/workplaces/${id}`, { method: 'DELETE' });

export const deleteAllWorkplaces = (companyId) =>
  request(`/api/company/${companyId}/workplaces`, { method: 'DELETE' });

// Convenios — contrato y reglas por rol en src/utils/agreements.js.
//   · GET /api/agreement (sin barra final ni query: la doc no contempla
//     `?companyId=`): superadmin ve todos; el resto, los de su empresa + los
//     globales (companyId = null). El llamador filtra SIEMPRE en cliente con
//     agreementsForCompany() — también cubre el backend antiguo, que devuelve
//     todos los convenios sin campo companyId.
//   · POST: el body debe llevar SIEMPRE companyId (número, o null = global
//     solo para superadmin). Constrúyelo con buildAgreementCreatePayload().
//   · PUT: parcial; companyId solo si lo cambia un superadmin. Constrúyelo
//     con buildAgreementUpdatePayload().
export const listAgreements = () => request('/api/agreement');

export const createAgreement = (agreement) =>
  request('/api/agreement', { method: 'POST', body: JSON.stringify(agreement) });

export const updateAgreement = (id, agreement) =>
  request(`/api/agreement/${id}`, { method: 'PUT', body: JSON.stringify(agreement) });

export const deleteAgreement = (id) =>
  request(`/api/agreement/${id}`, { method: 'DELETE' });

// ── Mensajería entre usuarios ──────────────────────────────────────────────
// API Galera (rev. de hoy):
//   POST /api/message                       individual
//   POST /api/message/workplace             colectivo a una sede
//   POST /api/message/company               colectivo a una empresa
//   POST /api/message/anonymous             denuncia anónima (sin senderId)
//   GET  /api/message/list/:id              listar recibidos por un usuario
//   GET  /api/message/list/anonymous/:id    listar denuncias anónimas recibidas
//   GET  /api/message/:id                   leer un mensaje (marca read=1)
export const sendMessage = ({ senderId, receiverId, content }) =>
  request('/api/message', {
    method: 'POST',
    body: JSON.stringify({ senderId, receiverId, content }),
  });

export const sendMessageToWorkplace = ({ senderId, workplaceId, content }) =>
  request('/api/message/workplace', {
    method: 'POST',
    body: JSON.stringify({ senderId, workplaceId, content }),
  });

export const sendMessageToCompany = ({ senderId, companyId, content }) =>
  request('/api/message/company', {
    method: 'POST',
    body: JSON.stringify({ senderId, companyId, content }),
  });

// Denuncia anónima → solo admin empresa (type 1) o admin sede (type 2).
// El backend no almacena el sender; al leer, senderId vendrá como -1.
export const sendAnonymousMessage = ({ receiverId, content }) =>
  request('/api/message/anonymous', {
    method: 'POST',
    body: JSON.stringify({ receiverId, content }),
  });

export const listMessages = (userId) =>
  request(`/api/message/list/${userId}`);

// Denuncias anónimas recibidas por un admin (type 1 o 2). senderId = -1.
export const listAnonymousMessages = (userId) =>
  request(`/api/message/list/anonymous/${userId}`);

// Marca el mensaje como leído al obtenerlo.
export const readMessage = (messageId) =>
  request(`/api/message/${messageId}`);

// Festivos de España por comunidad autónoma + año.
// Backend Galera: el id 0 es nacional (común a toda España). 1..19 son CCAA.
// NOTA: el endpoint fue renombrado de "holyday" (typo histórico) a "holiday"
// en el backend. Mantenemos la API del frontend igual.
export const listAutonomousCommunities = () =>
  request('/api/holiday/spain/acList');

// Festivos de UNA comunidad autónoma para un año (id=0 son nacionales).
export const getHolidays = (communityId, year) =>
  request(`/api/holiday/spain/${communityId}/${year}`);

// Festivos de TODAS las comunidades + nacionales para un año concreto.
// Cada item incluye `autonomousCommunity` (0 = nacional, 1..19 = CCAA).
export const getAllHolidaysForYear = (year) =>
  request(`/api/holiday/spain/${year}`);

// Crear festivo (sólo super). autonomousCommunity = 0 → festivo nacional.
// Nota: el backend acepta los campos en minúsculas; en mayúsculas devuelve 500.
export const addHoliday = ({ date, description, autonomousCommunity }) =>
  request('/api/holiday/spain', {
    method: 'POST',
    body: JSON.stringify({ date, description, autonomousCommunity }),
  });

// Eliminar festivo conocido su id.
export const deleteHoliday = (id) =>
  request(`/api/holiday/spain/${id}`, { method: 'DELETE' });

// ── Festivos de empresa ────────────────────────────────────────────────────
// Festivos propios de UNA empresa (patrón local, convenio, etc.). Se SUMAN a
// los oficiales (nacionales/CCAA) y sólo aplican a trabajadores de esa
// empresa. Gestión: admin empresa (su empresa) y superadmin (cualquiera).
//   GET    /api/companyHoliday/:companyId?years=2026,2027
//          → [{ id, companyId, date: 'YYYY-MM-DD', description }]
//   POST   /api/companyHoliday/:companyId/import
//          body { years: [2026], holidays: [{ date, description }] }
//          → { deleted, created, holidays }. REEMPLAZA los festivos de la
//          empresa de los años indicados. 400 → { message, errors: [{ index, field, message }] }
//          (el `errors` completo queda en ApiError.data).
//   DELETE /api/companyHoliday/:companyId/:id → { deleted: 1 }
// OJO: la librería compartida de festivos importa getCompanyHolidays desde
// aquí en ambos repos (app y web) — no renombrar.
const toYearsCsv = (years) => {
  const list = Array.isArray(years) ? years : (years != null ? [years] : []);
  return Array.from(new Set(
    list.map((y) => Number(y)).filter((y) => Number.isInteger(y))
  )).join(',');
};

export const getCompanyHolidays = (companyId, years) => {
  const csv = toYearsCsv(years);
  return request(`/api/companyHoliday/${encodeURIComponent(companyId)}${csv ? `?years=${csv}` : ''}`);
};

export const importCompanyHolidays = (companyId, { years, holidays }) =>
  request(`/api/companyHoliday/${encodeURIComponent(companyId)}/import`, {
    method: 'POST',
    body: JSON.stringify({ years, holidays }),
  });

export const deleteCompanyHoliday = (companyId, id) =>
  request(`/api/companyHoliday/${encodeURIComponent(companyId)}/${encodeURIComponent(id)}`, { method: 'DELETE' });

// Documentos (multipart). El backend expone el field `image` aunque acepta cualquier MIME.
//
// Comportamiento real verificado de la API Galera (cambios no reflejados en
// la doc al 100%):
//   - Sin `description` en el POST, devuelve 200 OK con un id pero NO graba
//     companyId/workplaceId/uploaderId — fila huérfana, invisible en
//     getFileList. Por eso siempre añadimos description con un fallback.
//   - companyId vacío o uploaderId no numérico → 500 (eso sí lo señala).
//   - Con todos los campos correctos → 200 OK con la fila completa.
//
// Validamos la respuesta y abortamos con un error claro si el backend nos
// devuelve un registro fantasma (sin companyId), para que la UI no muestre
// un "subido correctamente" mentiroso.
// Dado un nombre de archivo base y un Set de nombres ya ocupados, devuelve
// el primer nombre que no choque añadiendo " (1)", " (2)", etc. ANTES de
// la extensión. Ej: "foto.jpg" + {"foto.jpg"} → "foto (1).jpg".
//   - Mantenemos el separador como espacio simple para que coincida con
//     la convención de Windows ("Documento (1).pdf").
//   - Si no hay extensión, simplemente se añade el sufijo al final.
//   - Comparamos case-insensitive para evitar "Foto.JPG" vs "foto.jpg".
const resolveUniqueFilename = (baseName, takenNames) => {
  const taken = new Set(Array.from(takenNames || []).map((n) => String(n).toLowerCase()));
  if (!taken.has(String(baseName).toLowerCase())) return baseName;
  const m = String(baseName).match(/^(.*?)(\.[^.\\/]+)?$/);
  const stem = (m && m[1]) || baseName;
  const ext = (m && m[2]) || '';
  let i = 1;
  while (i < 10000) {
    const candidate = `${stem} (${i})${ext}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
    i += 1;
  }
  // Caso patológico (10k duplicados); añadimos timestamp para no romper.
  return `${stem} (${Date.now()})${ext}`;
};

export const uploadDocument = async ({ file, companyId, workplaceId, uploaderId, description }) => {
  const url = `${BASE_URL}/api/upload`;
  const token = getToken();

  // Validación previa en cliente: estos tres campos son obligatorios para que
  // el documento quede vinculado y aparezca en los listados.
  const cId = Number(companyId);
  const uId = Number(uploaderId);
  if (!Number.isFinite(cId) || cId <= 0) {
    throw new ApiError({ message: 'Falta companyId al subir el documento.', url, reason: 'client-validation' });
  }
  if (!Number.isFinite(uId) || uId <= 0) {
    throw new ApiError({ message: 'Falta uploaderId al subir el documento.', url, reason: 'client-validation' });
  }
  const wId = Number.isFinite(Number(workplaceId)) ? Number(workplaceId) : 0;
  // Sanea filename a ASCII puro: el parser del backend descarta el resto del
  // multipart si encuentra caracteres no-Latin1 en el filename del Content-
  // Disposition (registro fantasma). También quitamos espacios.
  let safeName = (file.name || 'documento')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '_');

  // Resolución de nombre único: si la empresa ya tiene un documento con el
  // mismo nombre, añadimos sufijo (1), (2)... antes de la extensión. Pedimos
  // el listado completo de la empresa (incluye todas las sedes) para evitar
  // colisiones a nivel global. Si el listado falla (red, permisos) seguimos
  // con el nombre original — peor caso, el admin verá dos archivos iguales
  // pero la subida no se bloquea.
  try {
    const existing = await listCompanyDocuments(cId).catch(() => []);
    const taken = (Array.isArray(existing) ? existing : [])
      .map((d) => d?.filename || d?.name || '')
      .filter(Boolean);
    safeName = resolveUniqueFilename(safeName, taken);
  } catch {
    // listado falló → seguimos con el nombre saneado original.
  }

  const desc = (description == null ? '' : String(description))
    .trim()
    .replace(/[^\x20-\x7E]/g, '?') // ASCII printable; el backend almacena raro lo no-ASCII
    || safeName
    || 'documento';

  const fd = new FormData();
  // Orden: text fields PRIMERO, archivo al final. Reproduce el orden con el
  // que el endpoint funciona estable vía curl. Algunos parsers (multer en
  // streaming) procesan los text fields antes de saber con qué destino guardar
  // el binario y se descontrolan si el archivo llega antes.
  fd.append('companyId', String(cId));
  fd.append('workplaceId', String(wId));
  fd.append('uploaderId', String(uId));
  fd.append('description', desc);
  fd.append('image', file, safeName);

  const res = await fetch(url, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 401 && token) {
      const code = codeFromBody(text);
      endSession(token, code);
      throw new ApiError({
        message: sessionEndedMessage(code),
        url, status: 401, bodySnippet: text.slice(0, 400), reason: 'session-ended',
      });
    }
    throw new ApiError({
      message: translateBackendMessage(null, res.status),
      url, status: res.status, bodySnippet: text.slice(0, 400),
    });
  }
  const data = await res.json().catch(() => ({}));
  // Si el backend acepta el POST pero descarta los campos (registro fantasma),
  // tratamos eso como error explícito para que la UI no muestre "OK" en falso.
  if (data?.companyId == null || data?.uploaderId == null) {
    throw new ApiError({
      message: 'No se pudo guardar el documento. Inténtalo de nuevo o avisa al administrador.',
      url,
      status: res.status,
      bodySnippet: JSON.stringify(data).slice(0, 400),
      reason: 'orphan-upload',
    });
  }
  return data;
};

export const listCompanyDocuments = (companyId) =>
  request(`/api/upload/getFileList/${companyId}`);

export const listWorkplaceDocuments = (companyId, workplaceId) =>
  request(`/api/upload/getFileList/${companyId}/${workplaceId}`);

// Devuelve la URL absoluta para descarga directa (incluye token vía header,
// así que la página la consume vía fetch+blob, no como href crudo).
export const downloadDocumentUrl = (id) => `${BASE_URL}/api/upload/${id}`;

export const downloadDocument = async (id) => {
  const url = downloadDocumentUrl(id);
  const token = getToken();
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
  if (!res.ok) {
    if (res.status === 401 && token) {
      const code = codeFromBody(await res.text().catch(() => ''));
      endSession(token, code);
      throw new ApiError({ message: sessionEndedMessage(code), url, status: 401, reason: 'session-ended' });
    }
    throw new ApiError({
      message: translateBackendMessage(null, res.status),
      url, status: res.status,
    });
  }
  return res.blob();
};

export const createEvent = ({ workerId, eventType, timestamp, geoInfo, approvalStatus, workplaceId }) =>
  request('/api/event/', {
    method: 'POST',
    body: JSON.stringify({
      workerId, eventType,
      timestamp: timestamp || new Date().toISOString(),
      geoInfo: geoInfo || null,
      approvalStatus: approvalStatus ?? APPROVAL_STATUS.APPROVED,
      // Multicentro: registra desde qué centro se ficha. Solo se envía si se
      // conoce (el backend lo acepta como opcional y valida la geo contra
      // TODOS los centros asignados del trabajador).
      ...(workplaceId != null ? { workplaceId } : {}),
    }),
  });

// Defensa client-side: si alguno de los parámetros falta o llega vacío,
// no construimos la URL (saldría con "//" y el backend devuelve 500). El
// ApiError sustituye la llamada para que el caller maneje el caso sin
// romper la pantalla — algunos callers ya lo capturan con .catch(() => []).
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const getEventsByRange = (workerId, startDate, endDate) => {
  if (workerId == null || workerId === '' || !ISO_DATE_RE.test(String(startDate)) || !ISO_DATE_RE.test(String(endDate))) {
    return Promise.reject(new ApiError({
      message: 'Faltan parámetros para consultar fichajes (worker o rango de fechas).',
      url: '/api/event/worker/<missing>',
      reason: 'client-validation',
    }));
  }
  return request(`/api/event/worker/${workerId}/${startDate}/${endDate}`);
};

export const getEvent = (id) =>
  request(`/api/event/${id}`);

// Workaround para un bug conocido del backend (PUT /api/event/:id): el
// handler hace algo tipo `if (req.body.approvalStatus) ...` que descarta
// valores falsy. Resultado:
//   - approvalStatus = 0  → IGNORADO (no se puede bajar a PENDING).
//   - geoInfo = null / "" → IGNORADO (no se puede limpiar el campo).
// Forzamos string para que el backend pase el check:
//   - approvalStatus 0 → "0" (el backend lo parsea como Number en BD).
//   - geoInfo null/""  → " " (un espacio; al leerlo el front lo trata
//     como vacío con un .trim() defensivo si hace falta).
const patchEventBodyForBackend = (event) => {
  const out = { ...event };
  if (Object.prototype.hasOwnProperty.call(out, 'approvalStatus') && out.approvalStatus === 0) {
    out.approvalStatus = '0';
  }
  if (Object.prototype.hasOwnProperty.call(out, 'geoInfo')
      && (out.geoInfo == null || out.geoInfo === '')) {
    out.geoInfo = ' ';
  }
  return out;
};

export const updateEvent = (id, event) =>
  request(`/api/event/${id}`, { method: 'PUT', body: JSON.stringify(patchEventBodyForBackend(event)) });

export const deleteEvent = (id) =>
  request(`/api/event/${id}`, { method: 'DELETE' });

// ── Correcciones de fichaje ────────────────────────────────────────────────
// Endpoint Galera:
//   POST /api/correction               body: { workerId, pikachuId, eventType, timestamp, reason }
//     - workerId: quien solicita
//     - pikachuId: id del pikachu (evento) original a corregir
//     - eventType, timestamp: valores CORRECTOS deseados
//     - reason: motivo
//   GET  /api/correction/worker/:workerId   (ojo: path con /worker/ aunque la doc decía sin él)
//   PUT  /api/correction/:id           body: { approvalStatus }   0=pend, 1=ok, 2=rej
//   Aprobar sustituye timestamp y eventType del pikachu original.
export const requestCorrection = ({ workerId, pikachuId, eventType, timestamp, reason }) =>
  request('/api/correction', {
    method: 'POST',
    // Forzamos approvalStatus=0 al crear: si el default del backend fuera 1
    // (aprobada), las solicitudes nuevas desaparecerían inmediatamente del
    // panel de aprobaciones. Mejor explícito que dependiente del default.
    body: JSON.stringify({
      workerId, pikachuId, eventType, timestamp, reason,
      approvalStatus: APPROVAL_STATUS.PENDING,
    }),
  });

export const listCorrections = (workerId) =>
  request(`/api/correction/worker/${workerId}`);

export const reviewCorrection = (id, approvalStatus) =>
  request(`/api/correction/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ approvalStatus }),
  });

// ── Jornada laboral por trabajador (workhours) ─────────────────────────────
// Endpoint Galera:
//   POST   /api/workhours                  body: { workerId, dayOfWeek, in1, out1, in2, out2 }
//     - dayOfWeek: 0=Lunes ... 6=Domingo
//     - in1/out1: turno mañana (HH:MM:SS)
//     - in2/out2: turno tarde (HH:MM:SS) o null si solo hay turno único
//     - POST con (workerId, dayOfWeek) ya existentes hace UPSERT (sobrescribe).
//   GET    /api/workhours/:workerId        listado de los slots (uno por día configurado)
//   DELETE /api/workhours/:workerId/:dayOfWeek
//
// Convenciones de producto (no del backend):
//   - Día sin row → DESCANSO. Si el trabajador pica, queda pendiente de
//     aprobar y el admin decide si es horas extras (eventType 0/1) o
//     compensación (eventType 8/9).
//   - Row con in1=00:00:00 y out1=00:00:00 (in2/out2 null) → HORARIO LIBRE.
//     Sin guard de hora de inicio en el Dashboard.
// Mantenemos el orden visual L-D que es el que el usuario español espera leer,
// pero el `value` de cada entrada es el índice que el backend usa internamente
// (0=Domingo .. 6=Sábado, alineado con Date.getDay() de JS y librerías comunes).
// Así los componentes que iteran sobre DAYS_OF_WEEK siguen igual: enseñan
// L,M,X,J,V,S,D y al guardar mandan el value correcto al backend.
export const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes',     short: 'Lun' },
  { value: 2, label: 'Martes',    short: 'Mar' },
  { value: 3, label: 'Miércoles', short: 'Mié' },
  { value: 4, label: 'Jueves',    short: 'Jue' },
  { value: 5, label: 'Viernes',   short: 'Vie' },
  { value: 6, label: 'Sábado',    short: 'Sáb' },
  { value: 0, label: 'Domingo',   short: 'Dom' },
];

// El day-of-week de JS (Date.getDay() → 0=Dom..6=Sáb) coincide ahora con
// la convención del backend, así que es identidad.
export const jsDowToBackend = (jsDow) => jsDow;

// Devuelve la entrada de DAYS_OF_WEEK que corresponde al value (índice backend).
// Necesario porque el array está ordenado L-D en la UI pero el value es el
// del backend (0=Dom..6=Sáb), así que NO se puede acceder por índice.
export const dayOfWeekMeta = (value) =>
  DAYS_OF_WEEK.find((d) => d.value === Number(value)) || { value, label: '?', short: '?' };

// Normaliza una hora a "HH:MM:SS". Acepta "HH:MM", "HH:MM:SS" o null.
const toHMS = (v) => {
  if (v == null || v === '') return null;
  const s = String(v).trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  return null;
};

// "Horario libre" se ELIMINÓ del producto (mayo 2026): se confundía con
// "fichaje en diferido". Helper se mantiene devolviendo siempre false para
// no romper consumidores antiguos. Las filas viejas con in1=out1=00:00 que
// puedan quedar en BD se tratan ahora como descanso (igual que si no
// existiera la fila). El cleanup definitivo se hace via DELETE de esas
// filas tras desplegar este cambio.
export const isFreeSchedule = (_slot) => false;

// ── Detección de fichajes en diferido ──────────────────────────────────────
// Un evento es "diferido" cuando se registró a una hora distinta de la que
// ocurrió: el `timestamp` es la hora "real" que pone el admin/trabajador en
// la solicitud, y `createdAt` es cuando se guardó en BD. Si difieren más de
// 60 segundos lo consideramos diferido.
const DEFERRED_TOLERANCE_MS = 60 * 1000;
export const isDeferred = (event) => {
  if (!event?.timestamp || !event?.createdAt) return false;
  const ts = new Date(event.timestamp).getTime();
  const ca = new Date(event.createdAt).getTime();
  if (!Number.isFinite(ts) || !Number.isFinite(ca)) return false;
  return Math.abs(ts - ca) > DEFERRED_TOLERANCE_MS;
};

// Texto de "registrado en HH:MM del DD/MM" para mostrar en badges/tooltips.
// Devuelve null si el evento no es diferido o le faltan campos.
export const deferredLabel = (event) => {
  if (!isDeferred(event)) return null;
  try {
    const d = new Date(event.createdAt);
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
};

// ── Audit trail de aprobaciones (workaround geoInfo) ──────────────────────
// El backend NO tiene columna `approvedBy` en la tabla de eventos, así que
// guardamos quién y cuándo aprobó/rechazó como un flag dentro de geoInfo:
//   ";apprby=Antonio%20Parrilla:2026-06-08T11:30:00Z"
// Mismo patrón que ya usamos para `;reqdelete=1` y `;extra=1`.
//
// El "quién" es el NOMBRE legible (URL-encoded), no el email — más amable
// para el usuario. Si el aprobador es Mechagodzilla (super) lo mostramos
// como "TemControl" para no exponer cuentas internas a los trabajadores.
//
// Quien escribe el flag: AprobacionesPage.handleEventApproval /
// VacationEditModal / AssignEventModal / AssignCompensationModal /
// EventEditModal. Quien lo lee y muestra: InformeHorarioView.
// Si no hay flag (evento aprobado antes de este cambio o sin auditoría),
// devuelve null y la UI no muestra el chip.

// Convierte el `approverUser` (puede ser objeto user o string legacy) en el
// nombre que va a aparecer en el chip. Super → "TemControl", el resto →
// nombre completo (con fallback a email si por algún motivo no hay nombre).
const approverDisplayName = (approver) => {
  if (!approver) return 'desconocido';
  if (typeof approver === 'string') return approver; // compat con strings antiguos
  if (Number(approver.type) === 5) return 'TemControl';
  return approver.name || approver.nombre || approver.email || 'desconocido';
};

export const parseApproverInfo = (geoInfo) => {
  if (!geoInfo) return null;
  const m = String(geoInfo).match(/;apprby=([^:;]+):([^;]+)/);
  if (!m) return null;
  // Antes guardábamos email crudo; ahora guardamos nombre URL-encoded.
  // decodeURIComponent es tolerante con strings ya planos: si lo intentamos
  // sobre un email tal cual, devuelve el mismo string. Solo capturamos el
  // raro caso de % aislado que invalidaría la decodificación.
  let who = m[1];
  try { who = decodeURIComponent(who); } catch { /* keep raw */ }
  let date = null;
  try {
    const d = new Date(m[2]);
    if (!Number.isNaN(d.getTime())) date = d;
  } catch { /* ignored */ }
  return { who, email: who, date };
};

// Label corto para mostrar en chips: "Aprobado por X" o "Aprobado por X · 8 jun".
// Acepta el `approvalStatus` para variar el verbo (aprobado/rechazado).
export const approverLabel = (event) => {
  const info = parseApproverInfo(event?.geoInfo);
  if (!info) return null;
  const isRejected = Number(event?.approvalStatus) === APPROVAL_STATUS.REJECTED;
  const verb = isRejected ? 'Rechazado' : 'Aprobado';
  const who = info.who || 'desconocido';
  if (!info.date) return `${verb} por ${who}`;
  const when = info.date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${verb} por ${who} · ${when}`;
};

// ── Motivo de fichaje manual / diferido ───────────────────────────────────
// Cuando un trabajador hace un fichaje manual desde el Dashboard puede
// escribir una nota opcional ("Reunión con cliente", "Olvidé fichar al
// entrar"…). El backend NO tiene columna para eso, así que la guardamos
// embebida en geoInfo:
//   ;motivo=Reunión con cliente
// El texto se URL-encodea para no chocar con `;` u otros caracteres
// usados por los demás flags (apprby, reqdelete, extra…).
export const buildGeoInfoWithMotivo = (currentGeoInfo, motivo) => {
  if (!motivo || !String(motivo).trim()) return currentGeoInfo || null;
  return appendToken(currentGeoInfo, 'motivo', encodeURIComponent(String(motivo).trim()));
};

export const parseMotivo = (geoInfo) => {
  if (!geoInfo) return null;
  const m = String(geoInfo).match(/;motivo=([^;]*)/);
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1] || null; }
};

// ── Ausencias justificadas: flag "recuperable" ────────────────────────────
// Cuando el admin aprueba una ausencia justificada (ABSENCE_* o SICK_LEAVE_*)
// puede marcarla como "recuperable" — significa que el trabajador debe
// recuperar esas horas a la empresa. El flag se persiste en geoInfo igual
// que los demás (apprby, motivo…):
//   ;recoverable=1  → horas recuperables (descontadas de la bolsa)
//   ;recoverable=0  → no recuperables (default, no afecta a la bolsa)
//
// Al aprobar un DÍA COMPLETO como recuperable, el frontend además crea un
// offset manual en /api/hourBalance con `offset` negativo (deuda del
// trabajador). Eso lo hace el caller; este helper solo se encarga del flag
// textual.
// Las ausencias POR HORAS se recuperan SIEMPRE desde 2026-09-22: la bolsa no
// reduce la jornada esperada, se aprueban con ;recoverable=1 y SIN offset. Un
// ;recoverable=0 antiguo en una ausencia por horas ya no tiene efecto.
export const buildGeoInfoWithRecoverable = (currentGeoInfo, recoverable) => {
  return appendToken(currentGeoInfo, 'recoverable', recoverable ? '1' : '0');
};

// Lee el flag. Devuelve true / false si está presente, null si no hay
// información (eventos antiguos pre-cambio o ausencias todavía pendientes
// que aún no se aprobaron por ningún admin).
export const parseRecoverableFlag = (geoInfo) => {
  if (!geoInfo) return null;
  const m = String(geoInfo).match(/;recoverable=([01])/);
  if (!m) return null;
  return m[1] === '1';
};

// Helper para construir el geoInfo con el flag de aprobador embebido.
// Acepta un objeto `user` (recomendado, para usar su nombre y tipo) o un
// string legacy. Reemplaza un flag anterior si lo hubiera (re-aprobación).
//   buildGeoInfoWithApprover(null, {name:'Antonio Parrilla', type:1}) →
//     ';apprby=Antonio%20Parrilla:2026-06-08T...'
//   buildGeoInfoWithApprover(null, {type:5}) →
//     ';apprby=TemControl:...'  // super = "TemControl" (no exponemos cuenta)
//   buildGeoInfoWithApprover('lat:38;lng:-1', userObj) →
//     'lat:38;lng:-1;apprby=...'
//
// Usado por: AprobacionesPage.handleEventApproval, AssignEventModal,
// AssignCompensationModal, EventEditModal. BulkEventImportModal queda
// fuera adrede — los fichajes importados no tienen aprobador atribuible.
export const buildGeoInfoWithApprover = (currentGeoInfo, approver) => {
  const who = approverDisplayName(approver);
  return appendToken(currentGeoInfo, 'apprby', `${encodeURIComponent(who)}:${new Date().toISOString()}`);
};

// ── Audit trail de EDICIÓN ────────────────────────────────────────────────
// Cuando un admin edita un evento (hora, tipo) SIN cambiar su estado de
// aprobación, no es una re-aprobación: es una corrección. Usamos un flag
// distinto (;modby=) para no pisar al aprobador original. Si el admin
// edita Y cambia el estado, también se aplica ;apprby= (lo aplica el caller).
// Como con ;apprby=, super → "TemControl", el resto → nombre completo.
export const buildGeoInfoWithModifier = (currentGeoInfo, modifier) => {
  const who = approverDisplayName(modifier);
  return appendToken(currentGeoInfo, 'modby', `${encodeURIComponent(who)}:${new Date().toISOString()}`);
};

export const parseModifierInfo = (geoInfo) => {
  if (!geoInfo) return null;
  const m = String(geoInfo).match(/;modby=([^:;]+):([^;]+)/);
  if (!m) return null;
  let who = m[1];
  try { who = decodeURIComponent(who); } catch { /* keep raw */ }
  let date = null;
  try {
    const d = new Date(m[2]);
    if (!Number.isNaN(d.getTime())) date = d;
  } catch { /* ignored */ }
  return { who, email: who, date };
};

export const modifierLabel = (event) => {
  const info = parseModifierInfo(event?.geoInfo);
  if (!info) return null;
  const who = info.who || 'desconocido';
  if (!info.date) return `Editado por ${who}`;
  const when = info.date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  return `Editado por ${who} · ${when}`;
};

export const getWorkHours = (workerId) =>
  request(`/api/workhours/${workerId}`);

// POST upsert: el backend sobreescribe la fila si ya existe (workerId, dayOfWeek).
//
// `scheduleVersionId` es OPCIONAL. Si se pasa, el backend asocia el registro a
// esa versión concreta (útil para crear/editar horarios HISTÓRICOS desde el
// modal de "Histórico de Jornadas"). Si se omite, el backend asocia el
// registro a la versión vigente HOY del trabajador (comportamiento anterior).
// El backend espera el campo con el nombre `scheduleId` en el body — el
// frontend lo llama internamente `scheduleVersionId` por consistencia con
// el resto del modelo, pero al serializarlo lo renombramos.
export const upsertWorkHours = ({ workerId, dayOfWeek, in1, out1, in2, out2, scheduleVersionId }) =>
  request('/api/workhours', {
    method: 'POST',
    body: JSON.stringify({
      workerId,
      dayOfWeek,
      in1:  toHMS(in1),
      out1: toHMS(out1),
      in2:  toHMS(in2),
      out2: toHMS(out2),
      ...(scheduleVersionId != null ? { scheduleId: scheduleVersionId } : {}),
    }),
  });

export const deleteWorkHours = (workerId, dayOfWeek) =>
  request(`/api/workhours/${workerId}/${dayOfWeek}`, { method: 'DELETE' });

// Workhours vigentes en una fecha concreta — resuelve la `ScheduleVersion`
// activa en ese día y devuelve sus 7 slots. Es la pieza clave para que el
// cómputo de la bolsa de horas use el horario correcto cuando el trabajador
// ha tenido varios horarios a lo largo del tiempo (38h/sem → 40h/sem, etc.).
//
// Response:
//   { date: "YYYY-MM-DD", scheduleVersionId: N, workhours: [{ dayOfWeek, in1, ... }] }
export const getWorkHoursAtDate = (workerId, date) =>
  request(`/api/workhours/${workerId}/date/${date}`);

// ── Person Assignment (convenios por periodo) ──────────────────────────────
// Sustituye al campo legacy `Person.agreement`. Permite asignar un convenio
// con `validFrom` (y backend autocierra el anterior con `validTo`). Mientras
// no se hayan migrado todos los trabajadores, el endpoint `active` hace
// fallback al `Person.agreement` antiguo — así no rompemos el flujo actual
// durante la transición.

// Asigna un convenio a un trabajador a partir de `validFrom` (ISO YYYY-MM-DD).
// El backend cierra automáticamente la asignación anterior con validTo = día
// previo a validFrom. Usar este cuando el cambio es "desde ya en adelante".
// 404 "Agreement not found" → con el backend nuevo también es "convenio de
// otra empresa". El genérico "No se encontró el recurso solicitado" no dice
// nada al admin, así que lo sustituimos por un mensaje concreto (lo muestran
// toasts, errores del formulario y el detalle de la asignación masiva).
const withAgreementScopeError = (promise) =>
  promise.catch((err) => {
    if (isAgreementNotFoundError(err)) {
      throw new ApiError({
        message: AGREEMENT_NOT_IN_COMPANY_MESSAGE,
        url: err.url,
        status: err.status,
        bodySnippet: err.bodySnippet,
        reason: 'agreement-not-in-company',
        data: err.data,
      });
    }
    throw err;
  });

export const assignPersonAgreement = ({ personId, agreementId, validFrom }) =>
  withAgreementScopeError(request('/api/personAssignment/assign', {
    method: 'POST',
    body: JSON.stringify({ personId, agreementId, validFrom }),
  }));

// Asigna un convenio con un rango EXPLÍCITO [validFrom, validTo]. Útil para
// insertar tramos históricos (p.ej. "del 2024-01-01 al 2024-06-30 tenía el
// convenio Junior"). El backend auto-cierra solapamientos existentes.
export const assignPersonAgreementBounded = ({ personId, agreementId, validFrom, validTo }) =>
  withAgreementScopeError(request('/api/personAssignment/assign-bounded', {
    method: 'POST',
    body: JSON.stringify({ personId, agreementId, validFrom, validTo }),
  }));

// Histórico completo de asignaciones de un trabajador, con los datos del
// convenio joineados (assignAgreementName, assignMonthlyHours, etc.).
export const getPersonAssignmentHistory = (personId) =>
  request(`/api/personAssignment/${personId}/history`);

// Asignación activa HOY (con fallback al Person.agreement legacy si no hay).
export const getActivePersonAssignment = (personId) =>
  request(`/api/personAssignment/${personId}/active`);

// Horas mensuales esperadas en una fecha concreta. Útil para el cómputo
// del cupo cuando el trabajador ha tenido convenios distintos.
export const getExpectedMonthlyHours = (personId, date) =>
  request(`/api/personAssignment/${personId}/expected/${date}`);

// Listado global de todas las asignaciones (uso administrativo).
export const listAllPersonAssignments = () =>
  request('/api/personAssignment/');

export const deletePersonAssignment = (assignmentId) =>
  request(`/api/personAssignment/${assignmentId}`, { method: 'DELETE' });

// ── Schedule Version (horarios por periodo) ────────────────────────────────
// Igual idea que PersonAssignment pero para los workhours. Cada trabajador
// puede tener varias versiones de horario a lo largo del tiempo (p.ej. pasar
// de 38h/sem a 40h/sem). Cada Workhours row queda enlazada a una scheduleVersionId.

// Crea una versión nueva ABIERTA (sin validTo) con `validFrom`. Backend
// cierra la versión anterior con validTo = día previo. Tras crearla, los
// siguientes POST a /api/workhours quedan ligados a esta versión.
// Backend renombró este endpoint en el rediseño bounded/unbounded — antes
// estaba en POST /api/scheduleVersion/.
export const createScheduleVersion = ({ workerId, validFrom }) =>
  request('/api/scheduleVersion/create-unbounded', {
    method: 'POST',
    body: JSON.stringify({ workerId, validFrom }),
  });

// Crea una versión CERRADA con `validFrom + validTo` explícitos. Útil para
// insertar tramos históricos (p.ej. "del 2024-01-01 al 2024-06-30 trabajaba
// 38h/semana"). Backend autocierra los solapamientos existentes.
export const createScheduleVersionBounded = ({ workerId, validFrom, validTo }) =>
  request('/api/scheduleVersion/create-bounded', {
    method: 'POST',
    body: JSON.stringify({ workerId, validFrom, validTo }),
  });

export const getActiveScheduleVersion = (workerId) =>
  request(`/api/scheduleVersion/active/${workerId}`);

// Histórico completo con los slots de cada versión (assignSlots).
export const getScheduleVersionHistory = (workerId) =>
  request(`/api/scheduleVersion/history/${workerId}`);

export const listAllScheduleVersions = () =>
  request('/api/scheduleVersion/');

export const deleteScheduleVersion = (versionId) =>
  request(`/api/scheduleVersion/${versionId}`, { method: 'DELETE' });

// ── Bolsa de horas (manual offsets) ─────────────────────────────────────────
// El backend admite offsets manuales para mover el balance de un trabajador.
// Convención de signo:
//   · offset > 0 → la EMPRESA debe horas al TRABAJADOR (crédito a su favor).
//   · offset < 0 → el TRABAJADOR debe horas a la empresa.
//
// IMPORTANTE: la doc dice "POST /api/hourBalance/:workerId" pero la realidad
// es "POST /api/hourBalance" (workerId va en el body). Probado en producción.
//
// El campo `offset` es FLOAT en horas (1.5 = una hora y media). El backend
// devuelve el offset creado con su id, que es el que se usa para borrar.
export const getHourBalance = (workerId) =>
  request(`/api/hourBalance/${workerId}`);

export const addHourBalance = ({ workerId, date, reason, offset }) =>
  request('/api/hourBalance', {
    method: 'POST',
    body: JSON.stringify({
      workerId: Number(workerId),
      date,                 // YYYY-MM-DD
      reason: reason || '',
      offset: Number(offset),
    }),
  });

export const deleteHourBalance = (offsetId) =>
  request(`/api/hourBalance/${offsetId}`, { method: 'DELETE' });

// Sincroniza la semana completa de un trabajador.
// `weekArray` es un array de hasta 7 entradas con shape:
//   { dayOfWeek, in1, out1, in2, out2, isOff }
// - isOff:true   → DELETE de ese dayOfWeek (queda como descanso).
// - resto        → upsert con los valores indicados.
// Si en weekArray faltan días, NO se tocan (idempotencia parcial).
//
// `scheduleVersionId` opcional: cuando se quieren asociar los upserts a una
// versión concreta (caso típico: histórico). Si se omite, el backend asocia
// a la versión vigente HOY del trabajador.
export const saveWeekSchedule = async (workerId, weekArray, scheduleVersionId = null) => {
  const results = [];
  for (const day of weekArray) {
    if (day.isOff) {
      try { await deleteWorkHours(workerId, day.dayOfWeek); }
      catch (err) {
        // Si no existía aún, el DELETE puede dar 404 — lo tratamos como éxito.
        if (err?.status !== 404) throw err;
      }
      results.push({ dayOfWeek: day.dayOfWeek, action: 'deleted' });
    } else {
      await upsertWorkHours({
        workerId,
        dayOfWeek: day.dayOfWeek,
        in1: day.in1, out1: day.out1, in2: day.in2, out2: day.out2,
        scheduleVersionId,
      });
      results.push({ dayOfWeek: day.dayOfWeek, action: 'upserted' });
    }
  }
  return results;
};
